'use client';

import { Body } from '@depop/web-thrift-ui/Typography';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { Price } from '@/components/Price';
import {
  ProductCard,
  type Props as ProductCardProps,
} from '@/components/ProductCard';
import { ProductCardLike } from '@/components/ProductCard/ProductCardLike';
import { SecondaryPriceLine } from '@/components/SecondaryPriceLine';
import { BOOSTED_TEXT_ANY_LOCALE } from '@/constants/boostedListings';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { toGuestLikeProduct } from '@/modules/guestLikes/helpers';
import {
  buildSearchProductImageProps,
  picturePreviewAdapter,
} from '@/modules/pictures/helpers';
import { composeProductImageDescription } from '@/modules/product/helpers/composeProductImageDescription';
import { getSizeString } from '@/modules/product/helpers/getSizeString';
import { type PresApiSearchProduct } from '@/modules/search/aliases';

type Props = {
  slug: PresApiSearchProduct['slug'];
  pricing: PresApiSearchProduct['pricing'];
  sizes?: PresApiSearchProduct['sizes'];
  pictures?: PresApiSearchProduct['pictures'];
  isBoosted?: PresApiSearchProduct['is_boosted'];
  brandName?: PresApiSearchProduct['brand_name'];
  brandId?: PresApiSearchProduct['brand_id'];
  attributes?: PresApiSearchProduct['attributes'];
  id: PresApiSearchProduct['id'];
  isLiked?: PresApiSearchProduct['is_liked'];
  handleClick?: () => void;
  disableLazyLoading?: boolean;
  product: PresApiSearchProduct;
};

export function BrowseProductCard({
  id,
  slug,
  pricing,
  sizes,
  brandName,
  brandId,
  attributes,
  pictures,
  isBoosted,
  isLiked,
  handleClick,
  disableLazyLoading = false,
  product,
}: Props) {
  const t = useTranslations('product');
  const sizeString = getSizeString({
    sizes,
    multipleSizesString: t('MultipleSizes'),
  });

  const conditionalProps: Partial<ProductCardProps> = {};

  const firstPicture = pictures?.[0];
  const preview = firstPicture ? picturePreviewAdapter(firstPicture) : {};
  const imageDescription = composeProductImageDescription({
    brandName,
    brandId,
    attributes,
  });
  const displayPrice = pricing.display_price;

  if (pictures && pictures.length > 1) {
    const hoverImageProps = buildSearchProductImageProps(pictures[1]);
    conditionalProps.HoverOverlay = () => (
      <LazyLoadImage {...hoverImageProps} alt={imageDescription} />
    );
  }

  return (
    <ProductCard
      productHref={`${slug}${isBoosted ? '?isBoostedView=true' : ''}`}
      preview={preview}
      alt={imageDescription}
      handleClick={handleClick}
      disableLazyLoading={disableLazyLoading}
      ProductAttributes={() => (
        <div className={styles.productAttributesContainer}>
          <div className={styles.productAttributes}>
            {brandName && (
              <Body variant="sm-regular" className={styles.brandName}>
                {brandName}
              </Body>
            )}
            {sizeString && (
              <Body variant="sm-regular" className={styles.sizeAttributeText}>
                {sizeString}
              </Body>
            )}
            <Price
              currencyCode={
                displayPrice.headline_price.currency as CurrencyCode
              }
              discountedPrice={
                displayPrice.strikethrough_price
                  ? displayPrice.headline_price.amount
                  : undefined
              }
              price={
                displayPrice.strikethrough_price?.amount ??
                displayPrice.headline_price.amount
              }
            />
            {displayPrice.secondary_price && (
              <SecondaryPriceLine
                secondaryPrice={displayPrice.secondary_price}
              />
            )}
          </div>
          {isBoosted && (
            <Body variant="sm-bold" className={styles.boostedTag}>
              {BOOSTED_TEXT_ANY_LOCALE}
            </Body>
          )}
        </div>
      )}
      Overlay={() => (
        <ProductCardLike
          productId={id}
          isLiked={isLiked}
          isBoosted={isBoosted}
          guestLikeProduct={toGuestLikeProduct(product)}
        />
      )}
      {...conditionalProps}
    />
  );
}
